/**
 * Wallet interaction error toolkit.
 *
 * Background: the wallet provider exposed by `@web3jskit/walletkit` routes every
 * `provider.request(...)` through a `postMessage`-based bridge whose pending
 * promise is only settled when a matching response message arrives. That layer
 * has NO timeout and NO "peer is gone" handling, so when the wallet extension is
 * disabled / invalidated mid-flow the request promise hangs forever and the
 * "Extension context invalidated." error is thrown inside the (now dead)
 * extension context, never reaching our page.
 *
 * Because no error reaches us in that case, a plain `try/catch` cannot interrupt
 * the flow. This module supplies the missing pieces:
 *
 *   - `probeWalletAlive`: an active, NON-interactive liveness probe (does not pop
 *     the wallet) bounded by a short timeout, used to fail fast before entering a
 *     blocking signature UI.
 *   - `raceWithAbort`: lets a user-facing "cancel" button reject an in-flight
 *     request promise so the UI can be reset (the underlying walletkit promise
 *     cannot be truly cancelled, but resetting our state is enough).
 *   - `classifyWalletError` / `getWalletErrorMessageKey`: map any wallet failure
 *     to a stable i18n key under `wallet.*`.
 */

/** EIP-1193 user-rejected error code. */
const USER_REJECTED_CODE = 4001;

/** EIP-3085 target chain has not been added to the wallet. */
const CHAIN_NOT_ADDED_CODE = 4902;

/** JSON-RPC method not implemented by the wallet / bridge. */
const METHOD_NOT_FOUND_CODE = -32601;

/** Default bound (ms) for the non-interactive liveness probe. */
export const WALLET_PROBE_TIMEOUT_MS = 4000;

/**
 * Classified wallet error codes. Each maps to a `wallet.*` i18n key via
 * {@link getWalletErrorMessageKey}.
 */
export type WalletErrorCode =
  | 'userRejected'
  | 'noProvider'
  | 'extensionInvalidated'
  | 'notResponding'
  | 'chainNotConfigured'
  | 'cancelled'
  | 'unknown';

/** Minimal EIP-1193 provider shape used by the probe. */
export interface ProviderLike {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
}

/**
 * Typed wallet error carrying a {@link WalletErrorCode}.
 *
 * Callers can map `error.code` (or the error itself) to a friendly i18n message
 * via {@link getWalletErrorMessageKey}.
 */
export class WalletError extends Error {
  readonly code: WalletErrorCode;

  constructor(code: WalletErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'WalletError';
    this.code = code;
  }
}

/**
 * Read the EIP-1193 / JSON-RPC error code out of any thrown value.
 *
 * Wallet bridges reject with plain objects at least as often as with `Error`
 * instances, and some nest the original failure under `data.originalError`.
 * Accepts both shapes so callers never have to special-case them.
 */
export function getEip1193ErrorCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const err = error as {
    code?: unknown;
    data?: { originalError?: { code?: unknown } };
  };
  if (typeof err.code === 'number') return err.code;
  const nested = err.data?.originalError?.code;
  if (typeof nested === 'number') return nested;
  return undefined;
}

/**
 * Whether the error is "user actively rejected in the wallet" (EIP-1193 4001).
 *
 * Centralized here so login / signature / transaction call sites share one
 * implementation. Matches the numeric code first, then falls back to message
 * text for wallets that omit the code.
 */
export function isUserRejected(error: unknown): boolean {
  if (error instanceof WalletError) return error.code === 'userRejected';
  if (!error || typeof error !== 'object') return false;
  if (getEip1193ErrorCode(error) === USER_REJECTED_CODE) return true;
  const msg = (error as { message?: string }).message?.toLowerCase() ?? '';
  return msg.includes('user rejected') || msg.includes('user denied');
}

/**
 * Whether the error looks like a dead / invalidated extension context.
 *
 * This surfaces as `Extension context invalidated.` (Chromium) or similar
 * "context invalidated" / "receiving end does not exist" messages.
 */
export function isExtensionInvalidated(error: unknown): boolean {
  if (error instanceof WalletError) return error.code === 'extensionInvalidated';
  if (!error || typeof error !== 'object') return false;
  const msg = (error as { message?: string }).message?.toLowerCase() ?? '';
  return (
    msg.includes('extension context invalidated') ||
    msg.includes('context invalidated') ||
    msg.includes('receiving end does not exist')
  );
}

/**
 * Classify any thrown value into a {@link WalletErrorCode}.
 *
 * The numeric-code branch also covers plain-object rejections coming from
 * hosted wallet bridges, which would otherwise all collapse into `unknown`.
 */
export function classifyWalletError(error: unknown): WalletErrorCode {
  if (error instanceof WalletError) return error.code;
  if (isUserRejected(error)) return 'userRejected';
  if (isExtensionInvalidated(error)) return 'extensionInvalidated';
  const eip1193Code = getEip1193ErrorCode(error);
  if (
    eip1193Code === CHAIN_NOT_ADDED_CODE ||
    eip1193Code === METHOD_NOT_FOUND_CODE
  ) {
    return 'chainNotConfigured';
  }
  return 'unknown';
}

/**
 * Map a {@link WalletErrorCode} (or raw error) to its `wallet.*` i18n key.
 *
 * Usage: `toast.error(t(getWalletErrorMessageKey(error)))`.
 */
export function getWalletErrorMessageKey(
  codeOrError: WalletErrorCode | unknown
): string {
  const code: WalletErrorCode =
    typeof codeOrError === 'string'
      ? (codeOrError as WalletErrorCode)
      : classifyWalletError(codeOrError);

  switch (code) {
    case 'userRejected':
      return 'wallet.errorRejected';
    case 'noProvider':
      return 'wallet.errorNoProvider';
    case 'extensionInvalidated':
      return 'wallet.errorExtensionInvalidated';
    case 'notResponding':
      return 'wallet.errorNotResponding';
    case 'chainNotConfigured':
      return 'wallet.errorChainNotConfigured';
    case 'cancelled':
      return 'wallet.cancelled';
    case 'unknown':
    default:
      return 'wallet.errorUnknown';
  }
}

/**
 * Actively verify the wallet provider is alive WITHOUT popping the wallet.
 *
 * Races a lightweight, non-interactive call (`eth_chainId`, normally <100ms)
 * against a short timeout. This is the root-cause fix for the "disabled
 * extension hangs the flow" problem: it runs before any blocking signature UI so
 * a dead provider fails fast with a friendly error instead of hanging forever.
 *
 * It is intentionally NOT a timeout on the user's actual signing step.
 *
 * @throws {WalletError} `noProvider` when provider is missing,
 *   `extensionInvalidated` when the probe rejects with a context error, or
 *   `notResponding` when the probe neither resolves nor rejects in time.
 */
export async function probeWalletAlive(
  provider: ProviderLike | null | undefined,
  timeoutMs: number = WALLET_PROBE_TIMEOUT_MS
): Promise<void> {
  if (!provider || typeof provider.request !== 'function') {
    throw new WalletError('noProvider');
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new WalletError('notResponding')), timeoutMs);
  });

  try {
    await Promise.race([
      provider.request({ method: 'eth_chainId' }),
      timeout,
    ]);
  } catch (error) {
    if (error instanceof WalletError) throw error;
    if (isExtensionInvalidated(error)) throw new WalletError('extensionInvalidated');
    // A real provider that answers with any other error is still "alive";
    // treat unexpected probe failures as not-responding to stay on the safe side.
    throw new WalletError('notResponding');
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Race an in-flight wallet request against an abort signal.
 *
 * When the user clicks a "cancel" button on a waiting modal we cannot truly
 * cancel the underlying walletkit promise, but we can reject this race with
 * `WalletError('cancelled')` so the caller resets its UI and the user regains
 * control.
 */
export function raceWithAbort<T>(
  promise: Promise<T>,
  signal: AbortSignal
): Promise<T> {
  if (signal.aborted) {
    return Promise.reject(new WalletError('cancelled'));
  }
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new WalletError('cancelled'));
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      }
    );
  });
}
